import type { ShippingAddress } from '@/types/sales'

/**
 * The delivery address of a physical product (F-74), collected at checkout.
 *
 * Mirrors the API's own check (`shippingAddress.js`): the same required
 * fields, the same limits and the same postal-code formats, so a form that
 * passes here is not refused there. The API stays the authority.
 */
export type ShippingForm = Record<keyof ShippingAddress, string>
export type ShippingField = keyof ShippingAddress

export const emptyShipping = (country = 'BR'): ShippingForm => ({
  name: '', street: '', number: '', complement: '', district: '', city: '', state: '', postalCode: '', country, phone: '',
})

/** Countries the community sells in; the list the app's locales cover plus the usual neighbors. */
export const SHIPPING_COUNTRIES = ['BR', 'PT', 'US', 'MX', 'AR', 'ES', 'IT', 'DE', 'AT', 'CH', 'CL', 'CO', 'UY', 'GB', 'FR'] as const

const REQUIRED: ShippingField[] = ['name', 'street', 'number', 'district', 'city', 'state', 'postalCode', 'country', 'phone']

const LIMITS: Partial<Record<ShippingField, [number, number]>> = {
  name: [2, 120], street: [1, 160], number: [1, 20], complement: [0, 80], district: [1, 80], city: [1, 80], state: [1, 80], phone: [7, 20],
}

const POSTAL: Record<string, RegExp> = {
  BR: /^\d{5}-?\d{3}$/, PT: /^\d{4}-?\d{3}$/, US: /^\d{5}(-\d{4})?$/, MX: /^\d{5}$/, ES: /^\d{5}$/, IT: /^\d{5}$/,
  DE: /^\d{5}$/, AT: /^\d{4}$/, CH: /^\d{4}$/, AR: /^([A-Za-z]\d{4}[A-Za-z]{3}|\d{4})$/,
}

const clean = (v: string) => v.replace(/\s+/g, ' ').trim()

/** `80010000` → `80010-000` while typing a Brazilian CEP; other countries are left as typed. */
export const maskPostalCode = (country: string, value: string): string => {
  if (country !== 'BR') return value.slice(0, 12)
  const d = value.replace(/\D/g, '').slice(0, 8)
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d
}

/** The i18n key (namespace `products`, under `shipping.errors`) of the first problem per field; empty means valid. */
export const validateShipping = (form: ShippingForm): Partial<Record<ShippingField, string>> => {
  const errors: Partial<Record<ShippingField, string>> = {}
  for (const field of REQUIRED) {
    if (!clean(form[field])) errors[field] = 'shipping.errors.required'
  }
  for (const [field, [min, max]] of Object.entries(LIMITS) as [ShippingField, [number, number]][]) {
    const len = clean(form[field]).length
    if (!errors[field] && (len < min || len > max)) errors[field] = 'shipping.errors.invalid'
  }
  if (!errors.phone && !/^[0-9+()\-.\s]{7,20}$/.test(form.phone.trim())) errors.phone = 'shipping.errors.invalid'
  const country = clean(form.country).toUpperCase()
  if (!errors.country && !/^[A-Z]{2}$/.test(country)) errors.country = 'shipping.errors.invalid'
  if (!errors.postalCode) {
    const postal = clean(form.postalCode).toUpperCase()
    const rule = POSTAL[country]
    const ok = rule ? rule.test(postal) : /^[A-Z0-9][A-Z0-9 -]{1,10}[A-Z0-9]$/.test(postal)
    if (!ok) errors.postalCode = country === 'BR' ? 'shipping.errors.cep' : 'shipping.errors.postalCode'
  }
  return errors
}

export const toShippingPayload = (form: ShippingForm): ShippingAddress => ({
  name: clean(form.name),
  street: clean(form.street),
  number: clean(form.number),
  ...(clean(form.complement) ? { complement: clean(form.complement) } : {}),
  district: clean(form.district),
  city: clean(form.city),
  state: clean(form.state),
  postalCode: clean(form.postalCode).toUpperCase(),
  country: clean(form.country).toUpperCase(),
  phone: clean(form.phone),
})

/** The API names the failing field as `shippingAddress.<field>`; null when the error is about something else. */
export const shippingFieldOf = (param?: string | null): ShippingField | 'form' | null => {
  if (!param) return null
  if (param === 'shippingAddress') return 'form'
  return param.startsWith('shippingAddress.') ? (param.slice('shippingAddress.'.length) as ShippingField) : null
}
